import { useCallback, useEffect, useState } from "react";
import "./styles/globals.css";

import { Button } from "./components/ui/button";
import { AppShell } from "./components/ui-shell/AppShell";
import { TabNavigation } from "./components/ui-shell/TabNavigation";
import { TabPanel } from "./components/ui-shell/TabPanel";
import { LoginPage } from "./features/auth/LoginPage";
import { useAuthSession } from "./features/auth/useAuthSession";
import { VerifyFatturePolizzeTab } from "./features/reconcile/VerifyFatturePolizzeTab";
import { StatusTabPlaceholder } from "./features/status/StatusTabPlaceholder";
import type { DocumentKind } from "./features/status/status-domain";
import { useActiveRun } from "./features/status/useActiveRun";
import { useRunsHistory } from "./features/status/useRunsHistory";
import { useStatusViewState } from "./features/status/useStatusViewState";
import { UploadTabPlaceholder } from "./features/upload/UploadTabPlaceholder";
import type { ApiError } from "./lib/api/errors";
import {
  deletePolizzaRun,
  deleteRun,
  getProcessingStatus,
} from "./lib/api/extract";

export function App() {
  const [hasActiveRuns, setHasActiveRuns] = useState(false);
  const {
    activeTab,
    runHistoryQuery,
    setActiveTab,
    setRunHistoryQuery,
    resetViewState,
  } = useStatusViewState();

  const {
    authStatus,
    authSession,
    authBusy,
    authErrorMessage,
    authInfoMessage,
    login,
    logout,
    markUnauthorized,
  } = useAuthSession();

  function handleUnauthorized(message?: string) {
    resetRunState();
    clearRuns();
    resetViewState();
    markUnauthorized(message);
  }

  const {
    runs,
    totalRuns,
    pageCount,
    isRefreshingRuns,
    refreshRuns,
    clearRuns,
  } = useRunsHistory({
    onUnauthorized: handleUnauthorized,
    query: runHistoryQuery,
  });

  const {
    runStatus,
    analysis,
    polizzaAnalysis,
    isSubmitting,
    isPollingActive,
    downloadingPdfRunId,
    isFetchingCompletedResult,
    completedResultError,
    uploadErrorMessage,
    statusErrorMessage,
    clearUploadError,
    resetRunState,
    submitExtract,
    inspectRun,
    downloadRunPdf,
    retryCompletedResult,
    refreshRunsWithStatus,
  } = useActiveRun({
    isAuthenticated: authStatus === "authenticated" && Boolean(authSession),
    onUnauthorized: handleUnauthorized,
    refreshRuns,
  });

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    if (!authSession) return;
    void refreshRunsWithStatus();
  }, [authSession, authStatus, refreshRunsWithStatus]);

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    if (!authSession) return;
    if (activeTab !== "status") return;
    void refreshRunsWithStatus();
  }, [activeTab, authSession, authStatus, refreshRunsWithStatus]);

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    if (!authSession) return;

    let cancelled = false;

    async function pollProcessingStatus() {
      try {
        const response = await getProcessingStatus();
        if (!cancelled) {
          setHasActiveRuns(response.has_active_runs);
        }
      } catch {
        // Silently ignore polling errors and preserve the last known state.
      }
    }

    void pollProcessingStatus();
    const interval = window.setInterval(
      () => void pollProcessingStatus(),
      3000,
    );

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [authSession, authStatus]);

  const handleLogout = useCallback(() => {
    resetRunState();
    clearRuns();
    resetViewState();
    logout();
  }, [clearRuns, logout, resetRunState, resetViewState]);

  const handleSelectRun = useCallback(
    async (runId: number, kind: DocumentKind) => {
      await inspectRun(runId, kind);
      setActiveTab("status", "push");
    },
    [inspectRun, setActiveTab],
  );

  const handleDeleteRun = useCallback(
    async (runId: number, kind: DocumentKind) => {
      try {
        if (kind === "fatture_ddt") {
          await deleteRun(runId);
        } else {
          await deletePolizzaRun(runId);
        }

        const isDeletingActiveRun =
          runStatus?.id === runId && runStatus.kind === kind;

        if (isDeletingActiveRun) {
          resetRunState();
        }

        const refreshResult = await refreshRuns();

        if (!refreshResult.ok && refreshResult.errorMessage) {
          throw new Error(refreshResult.errorMessage);
        }
      } catch (error) {
        const apiError = error as ApiError;
        if (apiError.status === 401) {
          resetRunState();
          clearRuns();
          resetViewState();
          markUnauthorized(apiError.message);
          throw error;
        }

        throw new Error(
          apiError.message ??
            "Impossibile rimuovere l'elemento selezionato dallo storico.",
        );
      }
    },
    [
      clearRuns,
      markUnauthorized,
      refreshRuns,
      resetRunState,
      resetViewState,
      runStatus,
    ],
  );

  if (authStatus === "checking") {
    return (
      <main className="login-page">
        <p className="login-page__loading">Verifica sessione in corso...</p>
      </main>
    );
  }

  if (authStatus !== "authenticated" || !authSession) {
    return (
      <LoginPage
        onLogin={login}
        busy={authBusy}
        errorMessage={authErrorMessage}
        infoMessage={authInfoMessage}
      />
    );
  }

  return (
    <AppShell
      title="Console Documenti"
      helperText="Carica PDF di fatture/DDT o polizze di carico, monitora l'elaborazione e verifica addebiti duplicati."
      headerAction={
        <Button type="button" variant="outline" onClick={handleLogout}>
          Esci
        </Button>
      }
      tabBar={
        <TabNavigation
          activeTab={activeTab}
          onTabChange={(nextTab) => setActiveTab(nextTab, "push")}
        />
      }
    >
      <div aria-live="polite">
        <TabPanel tabId="upload" activeTab={activeTab}>
          <UploadTabPlaceholder
            onSubmitExtract={submitExtract}
            onSubmitted={() => setActiveTab("status")}
            busy={isSubmitting}
            hasActiveRuns={hasActiveRuns}
            errorMessage={uploadErrorMessage}
            onResetError={clearUploadError}
          />
        </TabPanel>
        <TabPanel tabId="status" activeTab={activeTab} mountMode="lazy">
          <StatusTabPlaceholder
            runStatus={runStatus}
            analysis={analysis}
            polizzaAnalysis={polizzaAnalysis}
            isPollingActive={isPollingActive}
            isFetchingCompletedResult={isFetchingCompletedResult}
            completedResultError={completedResultError}
            errorMessage={statusErrorMessage}
            runs={runs}
            totalRuns={totalRuns}
            pageCount={pageCount}
            runsQuery={runHistoryQuery}
            refreshingRuns={isRefreshingRuns}
            downloadingPdfRunId={downloadingPdfRunId}
            hasActiveRuns={hasActiveRuns}
            onRunsQueryChange={setRunHistoryQuery}
            onRefreshRuns={refreshRunsWithStatus}
            onSelectRun={handleSelectRun}
            onDeleteRun={handleDeleteRun}
            onDownloadRunPdf={downloadRunPdf}
            onRetryCompletedResult={retryCompletedResult}
            onGoToUpload={() => setActiveTab("upload", "push")}
          />
        </TabPanel>
        <TabPanel tabId="verify" activeTab={activeTab} mountMode="lazy">
          <VerifyFatturePolizzeTab
            isActive={activeTab === "verify"}
            onUnauthorized={handleUnauthorized}
          />
        </TabPanel>
      </div>
    </AppShell>
  );
}

export default App;
